import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play, Star } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import { BookingSection } from "@/components/CTA";
import { trackEvent } from "@/lib/analytics";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="flex gap-0.5" role="img" aria-label={`Valutazione ${value} su 5`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        aria-hidden="true"
        strokeWidth={1.5}
        className={`${size} ${i < Math.round(value) ? "fill-leaf text-leaf" : "text-ink/20"}`}
      />
    ))}
  </span>
);

// Carosello recensioni: autoplay lento, pausabile, navigazione manuale.
const ReviewsCarousel = ({ reviews }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [playing, setPlaying] = useState(true);
  const [hovering, setHovering] = useState(false);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !playing || hovering) return undefined;
    const id = setInterval(() => emblaApi.scrollNext(), 6500);
    return () => clearInterval(id);
  }, [emblaApi, playing, hovering]);

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      data-testid="reviews-carousel"
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="-ml-5 flex">
          {reviews.map((review, i) => (
            <div
              key={i}
              className="min-w-0 shrink-0 grow-0 basis-full pl-5 sm:basis-1/2 lg:basis-1/3"
            >
              <article className="flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-7">
                <Stars value={review.rating || 0} />
                <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/70">{review.text}</p>
                <div className="mt-6 border-t border-ink/10 pt-4">
                  <p className="text-sm font-semibold text-ink">{review.author}</p>
                  <p className="mt-1 text-xs text-ink/40">
                    {review.relativeTime ? `${review.relativeTime} · ` : ""}Google
                  </p>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 flex items-center gap-3">
        <button
          type="button"
          onClick={scrollPrev}
          data-testid="reviews-prev-button"
          aria-label="Recensioni precedenti"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-200 hover:border-leaf-deep hover:text-leaf-deep"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={scrollNext}
          data-testid="reviews-next-button"
          aria-label="Recensioni successive"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-200 hover:border-leaf-deep hover:text-leaf-deep"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          data-testid="reviews-autoplay-toggle"
          aria-label={playing ? "Metti in pausa lo scorrimento automatico" : "Riprendi lo scorrimento automatico"}
          className="ml-2 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:border-leaf-deep hover:text-leaf-deep"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {playing ? "Pausa" : "Riprendi"}
        </button>
      </div>
    </div>
  );
};

export default function Recensioni() {
  useSEO(SEO.recensioni);

  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-16 sm:pb-24">
        <Fade>
          <p className="eyebrow text-leaf-deep">Recensioni</p>
        </Fade>
        <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          <Lines delay={0.2} lines={["Cosa dicono", <em key="e" className="text-leaf-deep">i pazienti</em>]} />
        </h1>
        <Fade delay={0.6}>
          <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
            Le recensioni pubblicate dai pazienti su Google e su MioDottore: parole reali, con nome
            e valutazione verificabili sulle piattaforme originali.
          </p>
        </Fade>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Recensioni su Google">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Recensioni su Google</h2>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-ink/60">
              Le recensioni dei pazienti — con nome, valutazione e testo reali — sono pubblicate
              sul profilo Google e possono essere lette direttamente lì, nella loro forma
              originale.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-12 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl text-xs leading-relaxed text-ink/40">
                Le recensioni sono contenuti pubblicati su Google dai rispettivi autori.
              </p>
              <a
                href={SITE.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-google-button"
                onClick={() => trackEvent("reviews_google_click")}
                className="group inline-flex shrink-0 items-center gap-3 rounded-full border border-ink/20 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-leaf-deep hover:text-leaf-deep"
              >
                Leggi tutte le recensioni su Google
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10" aria-label="Recensioni su MioDottore">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Recensioni su MioDottore</h2>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-ink/60">
              Le recensioni dei pazienti sono consultabili anche sul mio profilo MioDottore, la
              piattaforma che utilizzo per le prenotazioni online.
            </p>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-miodottore-button"
              onClick={() => trackEvent("reviews_miodottore_click")}
              className="group mt-9 inline-flex items-center gap-3 rounded-full border border-ink/20 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-leaf-deep hover:text-leaf-deep"
            >
              Leggi tutte le recensioni su MioDottore
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      <BookingSection
        title="Hai letto le recensioni dei pazienti?"
        text="Il passo successivo è una prima visita: valuteremo insieme il percorso più adatto alle tue esigenze."
      />
    </>
  );
}
